// The shipped default My Desk layout — a STARTING POINT only (owner, N-090:
// "every manager gets their own canvas... the shipped default is only the
// starting point"). Once a person saves their own arrangement
// (client/src/lib/deskLayout.ts), this is never consulted for them again;
// "Reset to default" is the only way back to it.
//
// Order and sizes are the build spec's own (item 5), packed left-to-right,
// top-to-bottom with the SAME `firstFreeSpot` packer "Add panel" uses — so
// the default layout can never overlap itself, by construction, rather than
// by hand-checked column arithmetic. A manager's default simply filters this
// same ordered list to the panel types their role may see before packing —
// panels below an omitted one naturally pack into the space it would have
// used ("flows up", per the spec).
import type { Role } from '../lib/nav';
import { firstFreeSpot } from './gridPack';
import { SEED_BLOCK_ROWS, fixedLayoutFor } from './fixedBlock';

const FIXED_FOOTPRINT = {
  col: 0, row: 0, w: 12, h: SEED_BLOCK_ROWS,
};
import { catalogEntry, type DeskLayout, type DeskPanelInstance, type PanelConfig, type PanelType } from './panelCatalog';

interface DefaultEntry {
  type: PanelType;
  config?: PanelConfig;
}

// Exact order from the build spec: four-clinic strip, Pay Rate, New Hires,
// SPC, Needs attention, Canvas, People, Quick Docs, Birthdays, Pay & Hours.
//
// N-094 ("forward and file") added two more: `intake` sits right after
// `payRateAlerts` — both are "things waiting on a decision" — and
// `communications` sits among the general-visibility panels, since every
// mapped role gets one (ALL_ROLES), same as SPC/Birthdays.
// Owner, 2026-10-12: Intake left the Desk ("no idea what this is") — the
// Company → Intake page is untouched.
//
// Owner, 2026-10-06: Pay Rate is every role's now (a manager's default Desk
// gets it, their own clinic only) and New Hires is the director's too — the
// role filter in defaultLayoutFor picks that up with no change here.
const DEFAULT_ORDER: DefaultEntry[] = [
  { type: 'clinicStrip' },
  // Owner, 2026-09-29/30: nvRecruiter (with its new-applicant alert) near
  // the top, so a new application is the first thing a manager sees.
  { type: 'recruiting', config: { clinic: 'all' } },
  { type: 'payRateAlerts' },
  // Owner, 2026-09-10: the Pay & Hours table joins the shipped desk right
  // after Pay Rate (which keeps the raise Reject/Move controls).
  { type: 'payHoursTable' },
  { type: 'newHires' },
  { type: 'spcAlerts' },
  { type: 'needsAttention' },
  { type: 'communications' },
  { type: 'canvas' },
  { type: 'people' },
  { type: 'quickDocs' },
  { type: 'birthdays' },
  // Owner, 2026-10-06: My Mail and My Calendar open to everyone and on every
  // Desk — each person's own inbox and calendar.
  { type: 'myMail' },
  { type: 'myCalendar' },
  // 'all' resolves to every clinic the viewer may see — for a MANAGER that
  // is exactly their own one clinic, which is how this same entry produces
  // "manager: their clinic" without a manager-specific list of its own.
];

/** Build (and pack) the shipped default layout for one role. Pure and
 * deterministic — same role in, same layout out, every time. */
export function defaultLayoutFor(role: Role): DeskLayout {
  // Owner, 2026-10-12: the seed — the role's seed block (clinic strip + the
  // five always-on panels, fixedBlock.ts fixedLayoutFor(role); a manager's
  // is the compact one) on top, everything else packed below. This is also
  // what a MANAGER / DIRECTOR desk template is until Tomer saves one.
  const panels: DeskPanelInstance[] = [];
  for (const slot of fixedLayoutFor(role)) {
    const def = catalogEntry(slot.type as PanelType);
    if (!def || !def.roles.includes(role)) continue;
    panels.push({
      id: `default-${slot.type}`,
      type: slot.type as PanelType,
      w: slot.w as DeskPanelInstance['w'],
      h: slot.h as DeskPanelInstance['h'],
      col: slot.col,
      row: slot.row,
      ...(slot.config ? { config: { ...slot.config } } : {}),
    });
  }
  for (const entry of DEFAULT_ORDER) {
    if (fixedLayoutFor(role).some((s) => s.type === entry.type)) continue;
    const def = catalogEntry(entry.type);
    if (!def || !def.roles.includes(role)) continue;
    const { w } = def.defaultSize;
    // N-090c: never ship a default shorter than the type's own floor (today
    // only `clinicStrip` sets one) — belt-and-braces alongside the catalog's
    // own `defaultSize.h` already meeting it, so the two can never drift
    // back out of sync the way they did when the strip first clipped.
    const h = Math.max(def.defaultSize.h, def.minH ?? def.defaultSize.h) as typeof def.defaultSize.h;
    // Packed around the whole block's footprint, so nothing ever lands in
    // rows 0..7 even for a role that cannot see one of the fixed panels.
    const { col, row } = firstFreeSpot([...panels, FIXED_FOOTPRINT], w, h);
    panels.push({
      id: `default-${entry.type}`,
      type: entry.type,
      w,
      h,
      col,
      row,
      ...(entry.config ? { config: entry.config } : {}),
    });
  }
  return { version: 1, panels };
}
